import type { ReactNode } from "react";
import { IconButton } from "./IconButton.tsx";
import { Text } from "./Text.tsx";
import { cx } from "./tokens.ts";
import "./mailcal.css";

export type DockedPanelProps = {
  title: string;
  /** Folded to its title bar at the bottom edge. */
  minimized: boolean;
  /** Large, centered over a scrim (Gmail's full-screen compose). */
  expanded: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  onClose: () => void;
  labels: { minimize: string; restore: string; expand: string; collapse: string; close: string };
  /** The bottom bar (send, tools, discard). */
  footer: ReactNode;
  testID: string;
  children: ReactNode;
};

/**
 * Gmail's compose window: docked at the bottom right over the page without blocking it, with minimize, expand and
 * close in its title bar. Web only; iOS / Android show the same content in a sheet.
 */
export function DockedPanel(p: DockedPanelProps) {
  return (
    <>
      {p.expanded && !p.minimized ? <div className="b-docked-scrim" onClick={p.onExpand} /> : null}
      <section className={cx("b-docked", p.minimized && "is-minimized", p.expanded && !p.minimized && "is-expanded")} aria-label={p.title} data-testid={p.testID}>
        <header className="b-docked-head" onClick={p.minimized ? p.onMinimize : undefined}>
          <Text weight="semibold" lines={1}>
            {p.title}
          </Text>
          <span className="b-docked-tools">
            <IconButton icon={p.minimized ? "chevronUp" : "chevronDown"} size="sm" accessibilityLabel={p.minimized ? p.labels.restore : p.labels.minimize} onPress={p.onMinimize} testID={`${p.testID}-minimize`} />
            {p.minimized ? null : <IconButton icon="expand" size="sm" accessibilityLabel={p.expanded ? p.labels.collapse : p.labels.expand} onPress={p.onExpand} testID={`${p.testID}-expand`} />}
            <IconButton icon="close" size="sm" accessibilityLabel={p.labels.close} onPress={p.onClose} testID={`${p.testID}-close`} />
          </span>
        </header>
        {p.minimized ? null : (
          <>
            <div className="b-docked-body">{p.children}</div>
            <footer className="b-docked-foot">{p.footer}</footer>
          </>
        )}
      </section>
    </>
  );
}
